import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  Button,
  IconButton,
  CircularProgress,
  Box,
} from '@mui/material';
import { Search, Close } from '@mui/icons-material';
import { plainToClass } from 'class-transformer';
import { SupplierVm } from 'api/reception/supplier.vm';
import { getSuppliers } from 'api/reception/get-suppliers';

interface SupplierSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (supplierId: string, supplierName: string) => void;
}

const SupplierSelectionDialog: React.FC<SupplierSelectionDialogProps> = ({ open, onClose, onSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suppliers, setSuppliers] = useState<SupplierVm[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const fetchSuppliers = (offset: string | null = null, reset: boolean = false) => {
    setLoading(true);

    getSuppliers(offset || '', searchTerm, {
      success: (response) => {
        const supplierVms = plainToClass(SupplierVm, response.objects);
        setHasMore(response.nextOffset !== null && response.nextOffset !== '');
        setNextOffset(response.nextOffset);
        setSuppliers(reset ? supplierVms : [...suppliers, ...supplierVms]);
        setLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter fornecedores:', err);
        setLoading(false);
      },
    });
  };

  useEffect(() => {
    if (open) {
      fetchSuppliers(null, true);
    }
  }, [open, searchTerm]);

  const handleLoadMore = () => {
    if (!hasMore || !nextOffset) return;
    fetchSuppliers(nextOffset);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          Selecionar Fornecedor
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="normal"
          placeholder="Procurar fornecedores..."
          InputProps={{
            startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />,
          }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <List>
          {suppliers.map((supplier) => (
            <ListItem component="button" key={supplier.id} onClick={() => onSelect(supplier.id, supplier.name)}>
              <ListItemText primary={supplier.name} secondary={`Criado em: ${new Date(supplier.createdAt).toLocaleDateString('pt-PT')}`} />
            </ListItem>
          ))}
        </List>
        {loading && <CircularProgress size={24} />}
        {hasMore && !loading && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Button variant="outlined" onClick={handleLoadMore}>
              Carregar mais
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SupplierSelectionDialog;
